// "2D-Fenster" im Drucken-Modal: massstabgetreue Strichzeichnungen im CAD-Stil
// (nur sichtbare Kanten, echtes Hidden-Line-Removal) aus 5 festen Blickrichtungen
// (Draufsicht + Nord/Süd/Ost/West) — Teil der Mehrtafelprojektions-Funktion, siehe
// Plan /home/beno/.claude/plans/starry-booping-shore.md.
//
// Anders als die 3D-Fenster (print_plan_3d_view.js) gibt es hier KEINEN Live-/
// Dreh-Modus — jedes 2D-Fenster ist immer ein statisches, gebackenes Bild, das
// bei Richtungs-/Filteränderung neu gerendert wird. Dadurch entfällt die ganze
// Live-Instanz-/OrbitControls-/ViewCube-Maschinerie der 3D-Fenster komplett.
import { S } from '../app/app_state.js?v=20260817c';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { makeId } from '../utils/id_service.js?v=20260706d';
import { dockToBounds, DOCK_EDGE_DIRECTIONS, pinnedPositionForEdge, propagateGroupDrag, COLUMN_LOCK_DIRECTIONS, ROW_LOCK_DIRECTIONS, classifyDraufsichtSide } from './print_plan_window_snap.js?v=20260803a';
import { placementDirectionFor, viewDirectionForPlacement } from './print_plan_projection.js?v=20260803a';
import { maxWebGLRenderEdgePx, releaseWebGLRenderer } from '../render/webgl_renderer_lifecycle.js?v=20260807a';
// Dieselbe Serialisierungs-Queue wie die 3D-Fenster (statt einer eigenen,
// unkoordinierten) — beide Module bauen WebGLRenderer und mutieren denselben
// S.view3d.modalFilters/scene-Zustand über build3DScene(); zwei getrennte
// Queues hätten "gleichzeitig laufende Bakes" nur INNERHALB je eines Moduls
// verhindert, nicht zwischen 2D und 3D (WebGL-Context-Erschöpfung +
// gegenseitiges Überschreiben der Filter waren dadurch möglich).
import { queueSceneWork } from './print_plan_3d_view.js?v=20260904f';
import {
  applyPrintPlanSceneGroupFilter,
  selectedPrintPlanGroupBounds
} from './print_plan_group_filter.js?v=20260904c';
import {
  normalizePrintPlanGroupKeys,
  printPlanGroupSelectionSignature
} from './print_plan_group_filter_model.mjs?v=20260904c';

// Nur die für die Strichzeichnung wirklich benötigten 3D-Module — bewusst OHNE
// three_controls.js/viewcube.js (kein Orbit-Modus nötig), damit das reine
// Öffnen des Drucken-Modals nicht unnötig zusätzlichen 3D-Interaktions-Code lädt.
let modulesPromise = null;
function load2DModules() {
  if (!modulesPromise) {
    modulesPromise = Promise.all([
      import('../three/scene3d.js?v=20260904f'),
      import('../export/snapshot_controller.js?v=20260904f')
    ]).then(([scene3d, snapshotController]) => ({ scene3d, snapshotController }));
  }
  return modulesPromise;
}

const MIN_WINDOW_MM = 20;
const DEFAULT_WINDOW_WIDTH_MM = 90;
const DEFAULT_WINDOW_HEIGHT_MM = 60;
const PRINT_WINDOW_SNAPSHOT_DPI = 200;
const MM_PER_INCH = 25.4;
const DIRECTIONS = ['top', 'north', 'south', 'east', 'west', 'northeast', 'northwest', 'southeast', 'southwest'];
const DEFAULT_DIRECTION = 'south';
// Nachträgliche Bild-Rotation je Blickrichtung (im Uhrzeigersinn, in Grad) —
// KEINE Kamera-/Geometrieänderung (Kamera bleibt exakt wie in
// directionCameraSetup() bestimmt), nur das fertig gebackene Bild wird
// gedreht, siehe renderHiddenLineSnapshot()/rotateCanvasImage(). Werte per
// visueller Prüfung durch den Auftraggeber festgelegt: Nord auf den Kopf
// gestellt (180°), Ost 90° gegen den Uhrzeigersinn (-90°), West 90° im
// Uhrzeigersinn (+90°), Süd/Draufsicht unverändert. Die 4 ISO-Diagonalen sind
// eine komplett neue Kamera-Geometrie (siehe directionCameraSetup()), noch
// ohne bekannten Rotationsbedarf — 0 bis zur visuellen Prüfung.
const WINDOW_IMAGE_ROTATION_DEG = { top: 0, north: 180, south: 0, east: -90, west: 90, northeast: 0, northwest: 0, southeast: 0, southwest: 0 };
// Fallback für renderHiddenLineSnapshot()'s Kamera-Zentrierung, falls noch nie
// eine echte lastBoundsMm berechnet wurde (Druck-Bake vor dem ersten
// renderPreview()-Tick) — analog zum bestehenden bounds-Fallback dort.
const FALLBACK_BOUNDS_MM = { left: -1000, top: -1000, right: 1000, bottom: 1000, heightMm: 2000, depthMm: 2000, originWorldMm: { x: 0, z: 0 }, n: 100 };

function directionLabel(direction) {
  switch (direction) {
    case 'top': return t('modal.drucken.wert.draufsicht', 'Draufsicht');
    case 'north': return t('modal.drucken.wert.nord', 'N');
    case 'south': return t('modal.drucken.wert.sued', 'S');
    case 'east': return t('modal.drucken.wert.ost', 'O');
    case 'west': return t('modal.drucken.wert.west', 'W');
    case 'northeast': return t('modal.drucken.wert.iso_no', 'ISO NO');
    case 'northwest': return t('modal.drucken.wert.iso_nw', 'ISO NW');
    case 'southeast': return t('modal.drucken.wert.iso_so', 'ISO SO');
    case 'southwest': return t('modal.drucken.wert.iso_sw', 'ISO SW');
    default: return direction;
  }
}

// Gleiche 4 Sichtbarkeits-Filter wie beim 3D-Fenster (WINDOW_FILTER_KEYS in
// print_plan_3d_view.js) — bewusst OHNE Preset/Helligkeit/Sonnenstand, da die
// ungeschattete Strichzeichnung davon optisch nicht beeinflusst wird.
const WINDOW_FILTER_KEYS = ['floor', 'doubleFloor', 'ceiling', 'doubleCeiling'];

function filterLabel(key) {
  switch (key) {
    case 'floor': return t('modal.drucken.wert.filter_boden', 'Boden');
    case 'doubleFloor': return t('modal.drucken.wert.filter_doppelboden', 'Doppelboden');
    case 'ceiling': return t('modal.drucken.wert.filter_decke', 'Decke');
    case 'doubleCeiling': return t('modal.drucken.wert.filter_doppeldecke', 'Doppeldecke');
    default: return key;
  }
}

let layerEl = null;
let windowsState = [];
let selectedWindowId = null;
let lastFitScale = 1;
// Projekt-Bounding-Box in Rahmen-mm ({left,top,right,bottom,heightMm}, Y-Höhe
// bereits durch den Druckmassstab geteilt) — von print_plan_modal.js via
// syncViewWindowsGeometry() durchgereicht (dort berechnet, da nur dort
// frameWorldOrigin()/previewCamera.n bekannt sind, siehe Modul-Kopfkommentar
// zur bewussten Import-Trennung). Trägt sowohl die massstabgetreue Draufsicht-
// Voreinstellung als auch das Andocken an die Zeichnungskante.
let lastBoundsMm = null;
let onGeometryCommit = null;
let onSidebarRefreshNeeded = null;
let onBeforeSelect = null;
let selectedPrintGroupKeys = null;
let selectedPrintGroupSignature = '*';
let viewWindowLifecycleGeneration = 0;

// ---------------------------------------------------------------------------
// Data model
// ---------------------------------------------------------------------------

function normalizeViewWindow(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const num = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : makeId('viewwindow2d'),
    type: '2d',
    xMm: num(raw.xMm, 10),
    zMm: num(raw.zMm, 10),
    widthMm: Math.max(MIN_WINDOW_MM, num(raw.widthMm, DEFAULT_WINDOW_WIDTH_MM)),
    heightMm: Math.max(MIN_WINDOW_MM, num(raw.heightMm, DEFAULT_WINDOW_HEIGHT_MM)),
    direction: DIRECTIONS.includes(raw.direction) ? raw.direction : DEFAULT_DIRECTION,
    dockedEdge: ['top', 'bottom', 'left', 'right'].includes(raw.dockedEdge) ? raw.dockedEdge : null,
    filters: {
      floor: raw.filters?.floor !== false,
      doubleFloor: raw.filters?.doubleFloor !== false,
      doubleCeiling: raw.filters?.doubleCeiling !== false,
      ceiling: raw.filters?.ceiling === true,
      // Standard AUS: die parametrische Wand (sketch_wall_render3d.js) soll
      // eine CAD-Projektion nicht standardmässig zukleistern.
      parametricWall: raw.filters?.parametricWall === true
    },
    snapshot: typeof raw.snapshot === 'string' && raw.snapshot.startsWith('data:image/') ? raw.snapshot : null,
    snapshotUpdatedAt: Number.isFinite(Number(raw.snapshotUpdatedAt)) ? Number(raw.snapshotUpdatedAt) : null,
    snapshotGroupSelectionSignature: typeof raw.snapshotGroupSelectionSignature === 'string'
      ? raw.snapshotGroupSelectionSignature
      : null
  };
}

function normalizeViewWindows(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter(entry => entry?.type === '2d').map(normalizeViewWindow).filter(Boolean);
}

function serializeViewWindows() {
  return windowsState.map(win => ({
    id: win.id,
    type: win.type,
    xMm: win.xMm,
    zMm: win.zMm,
    widthMm: win.widthMm,
    heightMm: win.heightMm,
    direction: win.direction,
    dockedEdge: win.dockedEdge,
    filters: { ...win.filters },
    snapshot: win.snapshot,
    snapshotUpdatedAt: win.snapshotUpdatedAt,
    snapshotGroupSelectionSignature: win.snapshotGroupSelectionSignature
  }));
}

function currentPrintGroupSelection() {
  return {
    selectedGroupKeys: selectedPrintGroupKeys === null ? null : [...selectedPrintGroupKeys],
    signature: selectedPrintGroupSignature
  };
}

function snapshotMatchesPrintGroupSelection(win) {
  if (!win?.snapshot) return false;
  if (win.snapshotGroupSelectionSignature === selectedPrintGroupSignature) return true;
  return !win.snapshotGroupSelectionSignature && selectedPrintGroupSignature === '*';
}

// "Das erste Fenster bestimmt den Ursprung": ein per Sidebar-Button neu
// eingefügtes 2D-Fenster ist immer eine Draufsicht, massstabgetreu exakt auf
// die Projekt-Bounding-Box gelegt (deckungsgleich mit der Zeichnung, falls die
// Planansicht geladen ist) — der Nutzer kann es danach frei wegziehen oder per
// N/S/O/W im Sidebar-Raster weitere, angrenzende Ansichten davon ableiten.
// Ohne bekannte Bounds (Modal noch nicht gerendert) Fallback auf die alte
// Kaskaden-Platzierung.
// Grösse eines 2D-Fensters wird laufend aus dem tatsächlichen Zeichnungsinhalt
// abgeleitet (nicht mehr manuell per Eckgriff eingestellt — siehe
// syncViewWindowsGeometry()) — dieselben Formeln, die früher nur einmalig bei
// der Erzeugung galten (hier/adjacentWindowRect()), gelten jetzt laufend, so
// dass die Box beim Zoomen automatisch mitwächst/-schrumpft und den Inhalt nie
// mehr clippt. Bei sehr flachen Grundrissen (z.B. eine einzelne gerade Wand
// mit kaum Tiefe) wird auf MIN_WINDOW_MM geklemmt — der Inhalt selbst bleibt
// dadurch unverzerrt, nur mit mehr Weissraum auf der geklemmten Achse.
//
// Pauschaler Rand (WINDOW_MARGIN_MM, je Seite) auf beiden Achsen: `boundsMm`
// (aus computeProjectBounds()) kennt nicht zwingend ALLES, was tatsächlich in
// die Strichzeichnung gebackt wird (z.B. SaniGuard-Bodenfüsse oder — falls per
// Filter eingeblendet — die parametrische Wand, siehe isExcludedFromBake()),
// dadurch wurden Füsse/Wand teils hart an der Fensterkante abgeschnitten statt
// vom Fenster mit umschlossen zu werden. Der Rand ist bewusst symmetrisch (auf
// BEIDEN Seiten je einer Achse) — da directionCameraSetup() unabhängig von der
// Fenster-Position immer auf das Zentrum von boundsMm zentriert (siehe dort),
// erscheint ein symmetrisch grösseres Fenster automatisch als symmetrischer
// Rand um den Inhalt, ganz ohne dass xMm/zMm zusätzlich angepasst werden
// müssten. Behebt das Clipping nicht vollständig (der Rand ist ein fester
// Wert, keine echte Bounds-Berechnung inkl. dieser Objekte — separates,
// bewusst nicht angegangenes Thema, siehe Plan-Notiz), deutet nicht erfasste
// Geometrie aber wenigstens an, statt sie hart abzuschneiden.
const WINDOW_MARGIN_MM = 5;
const DIAGONAL_DIRECTIONS = ['northeast', 'northwest', 'southeast', 'southwest'];

function autoSizeMm(direction, boundsMm) {
  // ISO-Diagonalen zeigen das ganze Gebäude schräg von oben/aussen —
  // gebraucht wird deshalb die DIAGONALE Grundriss-Ausdehnung als Breite
  // (schmäler würde die Ecken abschneiden), nicht die reine Breite oder Tiefe
  // wie bei einer geraden Elevation.
  const planDiagonalMm = Math.hypot(boundsMm.right - boundsMm.left, boundsMm.bottom - boundsMm.top);
  const widthMm = (DIAGONAL_DIRECTIONS.includes(direction)
    ? planDiagonalMm
    : direction === 'east' || direction === 'west'
      ? boundsMm.depthMm
      : boundsMm.right - boundsMm.left) + 2 * WINDOW_MARGIN_MM;
  const heightMm = (direction === 'top'
    ? boundsMm.bottom - boundsMm.top
    : boundsMm.heightMm) + 2 * WINDOW_MARGIN_MM;
  return { widthMm: Math.max(MIN_WINDOW_MM, widthMm), heightMm: Math.max(MIN_WINDOW_MM, heightMm) };
}

function createViewWindow() {
  if (lastBoundsMm) {
    const { widthMm, heightMm } = autoSizeMm('top', lastBoundsMm);
    // Zentrum ist unabhängig vom Klemmen immer das wahre Bounds-Zentrum —
    // verhindert, dass ein wegen MIN_WINDOW_MM hochgeklemmter dünner Inhalt an
    // den oberen/linken Rand rutscht statt zentriert zu bleiben.
    const centerX = (lastBoundsMm.left + lastBoundsMm.right) / 2;
    const centerZ = (lastBoundsMm.top + lastBoundsMm.bottom) / 2;
    return normalizeViewWindow({
      xMm: centerX - widthMm / 2,
      zMm: centerZ - heightMm / 2,
      widthMm,
      heightMm,
      direction: 'top'
    });
  }
  const cascade = windowsState.length * 10;
  return normalizeViewWindow({
    xMm: 10 + (cascade % 100),
    zMm: 10 + (cascade % 60),
    widthMm: DEFAULT_WINDOW_WIDTH_MM,
    heightMm: DEFAULT_WINDOW_HEIGHT_MM,
    direction: DEFAULT_DIRECTION
  });
}

// Erzeugt EIN neues, an das ausgewählte Fenster angrenzendes 2D-Fenster
// (N=oberhalb, S=unterhalb, O=rechts, W=links, deckungsgleich auf der
// Flucht-Achse) bzw. eine von ihm abgeleitete Draufsicht (Mitte des 3x3-
// Rasters) — die eigentliche Mehrtafelprojektions-Funktion ("bei Klick auf
// einen dieser Buttons wird relativ zum markierten Fenster eine weitere
// Ansicht generiert"). Ändert bewusst NICHT die Richtung des ausgewählten
// Fensters selbst (anders als der frühere Phase-D-Entwurf) — die Richtung
// eines Fensters ergibt sich ausschliesslich daraus, wie/wohin es erzeugt
// wurde, oder automatisch beim Andocken an eine Zeichnungskante.
function adjacentWindowRect(selected, direction) {
  // Grösse immer aus dem echten Zeichnungsinhalt abgeleitet (autoSizeMm) statt
  // von `selected` geerbt — robust auch dann, wenn `selected` gar nicht das
  // "kanonische" Fenster für diese Achse ist (z.B. Ost/West von einer
  // Draufsicht aus erzeugt: `selected.heightMm` wäre dort die Z-Spannweite,
  // nicht die für Ost/West nötige Y-Spannweite/Gebäudehöhe).
  const size = lastBoundsMm ? autoSizeMm(direction, lastBoundsMm) : null;
  const widthMm = size ? size.widthMm : Math.max(MIN_WINDOW_MM, direction === 'east' || direction === 'west' ? DEFAULT_WINDOW_HEIGHT_MM : selected.widthMm);
  const heightMm = size ? size.heightMm : Math.max(MIN_WINDOW_MM, DEFAULT_WINDOW_HEIGHT_MM);
  // Kreuz-Ausrichtung (siehe COLUMN_LOCK_DIRECTIONS/ROW_LOCK_DIRECTIONS): die
  // geteilte Achse (xMm für Nord/Süd/Draufsicht, zMm für West/Draufsicht/Ost)
  // wird von einem bereits EXISTIERENDEN Fenster derselben Gruppe geerbt,
  // falls eines existiert — nicht zwingend von `selected` (das könnte z.B.
  // bereits eine Nord-Ansicht sein, wenn von dort aus eine West-Ansicht
  // erzeugt wird; deren zMm gehört zur Spalten-, nicht zur Zeilen-Gruppe).
  // Erst wenn kein Gruppenmitglied existiert, Fallback auf `selected`.
  const columnAnchor = windowsState.find(w => COLUMN_LOCK_DIRECTIONS.includes(w.direction)) || selected;
  const rowAnchor = windowsState.find(w => ROW_LOCK_DIRECTIONS.includes(w.direction)) || selected;
  // `direction` bleibt die Blickrichtung/Kamera-Beschriftung des NEUEN
  // Fensters (unverändert, egal welche Projektionsmethode gilt) — nur die
  // Platzierung relativ zu `selected` folgt der (ggf. gegenüberliegenden)
  // Seite aus print_plan_projection.js. Siehe Modulkommentar dort für den
  // First-Angle-vs-Third-Angle-Hintergrund.
  switch (placementDirectionFor(direction)) {
    case 'north':
      return { xMm: columnAnchor.xMm, zMm: selected.zMm - heightMm, widthMm, heightMm, direction };
    case 'south':
      return { xMm: columnAnchor.xMm, zMm: selected.zMm + selected.heightMm, widthMm, heightMm, direction };
    // Vertikal auf die Mitte des Zeilenankers zentriert (nicht oberkantenbündig)
    // — Ost/West haben i.d.R. eine andere Höhe als die Draufsicht (Gebäudehöhe
    // vs. Grundriss-Tiefe), reine Oberkanten-Angleichung liess sie deshalb
    // sichtbar versetzt wirken.
    case 'east':
      return { xMm: selected.xMm + selected.widthMm, zMm: rowAnchor.zMm + rowAnchor.heightMm / 2 - heightMm / 2, widthMm, heightMm, direction };
    case 'west':
      return { xMm: selected.xMm - widthMm, zMm: rowAnchor.zMm + rowAnchor.heightMm / 2 - heightMm / 2, widthMm, heightMm, direction };
    // ISO-Ecken gehören zu keiner Kreuz-Gruppe (weder Spalte noch Zeile) —
    // direkt diagonal an `selected` andocken, kombiniert aus der jeweiligen
    // Nord/Süd- und Ost/West-Formel oben.
    case 'northwest':
      return { xMm: selected.xMm - widthMm, zMm: selected.zMm - heightMm, widthMm, heightMm, direction };
    case 'northeast':
      return { xMm: selected.xMm + selected.widthMm, zMm: selected.zMm - heightMm, widthMm, heightMm, direction };
    case 'southwest':
      return { xMm: selected.xMm - widthMm, zMm: selected.zMm + selected.heightMm, widthMm, heightMm, direction };
    case 'southeast':
      return { xMm: selected.xMm + selected.widthMm, zMm: selected.zMm + selected.heightMm, widthMm, heightMm, direction };
    case 'top':
      if (lastBoundsMm) {
        return { xMm: lastBoundsMm.left, zMm: lastBoundsMm.top, widthMm, heightMm, direction: 'top' };
      }
      return { xMm: selected.xMm, zMm: selected.zMm - selected.heightMm, widthMm: selected.widthMm, heightMm: selected.heightMm, direction: 'top' };
    default:
      return null;
  }
}

function generateAdjacentWindow(direction) {
  const selected = findWindow(selectedWindowId);
  if (!selected || !DIRECTIONS.includes(direction)) return;
  if (direction === 'top' && selected.direction === 'top') return;
  const rect = adjacentWindowRect(selected, direction);
  if (!rect) return;
  const win = normalizeViewWindow({ ...rect, filters: selected.filters });
  windowsState.push(win);
  const el = buildWindowElement(win);
  layerEl?.appendChild(el);
  selectViewWindow(win.id);
  onGeometryCommit?.();
  queueBake(win, el);
}

// ---------------------------------------------------------------------------
// Sidebar card
// ---------------------------------------------------------------------------

function viewsSidebarButtonHtml() {
  return `<button type="button" class="btn print-plan-sidebar-option" data-print-plan-action="add-2d-view">2D</button>`;
}

// ---------------------------------------------------------------------------
// Geometry sync (mm -> on-screen px, driven by the print modal's own fitScale)
// ---------------------------------------------------------------------------

function applyWindowGeometry(win, el) {
  el.style.left = `${win.xMm * lastFitScale}px`;
  el.style.top = `${win.zMm * lastFitScale}px`;
  el.style.width = `${win.widthMm * lastFitScale}px`;
  el.style.height = `${win.heightMm * lastFitScale}px`;
}

// Re-Bake-Trigger: die Kamera-Rahmung (directionCameraSetup) hängt direkt am
// Druckmassstab n (siehe scaleContextChanged() unten — Pan/originWorldMm
// beeinflusst weder Grösse noch Kamera-Zentrum, seit beide ausschliesslich
// aus originunabhängigen lastBoundsMm-Differenzen kommen). Mausrad-Zoom löst
// renderPreview() bei JEDEM Tick aus — ein Bake pro Tick wäre viel zu teuer
// (WebGL-Renderer + zwei Render-Pässe + PNG-Export), daher debounced Re-Bake
// aller Fenster erst nach einer kurzen Ruhephase.
const REBAKE_DEBOUNCE_MS = 400;
let rebakeAllTimer = null;
function scheduleRebakeAll() {
  if (rebakeAllTimer) clearTimeout(rebakeAllTimer);
  rebakeAllTimer = setTimeout(() => {
    rebakeAllTimer = null;
    windowsState.forEach(win => {
      const el = layerEl?.querySelector(`[data-view-window-id="${win.id}"]`);
      if (el) queueBake(win, el);
    });
  }, REBAKE_DEBOUNCE_MS);
}

// Nur noch der Druckmassstab n ist relevant — seit die Kamera-Zentrierung
// (directionCameraSetup) und die Auto-Fit-Grösse (autoSizeMm) beide
// ausschliesslich aus lastBoundsMm-Differenzen/-Feldern kommen, die
// nachweislich originunabhängig sind (right-left/bottom-top/heightMm/depthMm,
// siehe projectBoundsFrameMm() in print_plan_modal.js), beeinflusst reines
// Pannen weder Grösse noch gerenderten Inhalt mehr — nur eine echte
// Massstabsänderung erfordert einen (teuren) Re-Bake.
function scaleContextChanged(prev, next) {
  if (!prev || !next) return false;
  return Math.abs(prev.n - next.n) > 1e-6;
}

// Re-pinnt bereits an eine Bounds-Kante angedockte Fenster (win.dockedEdge)
// bei JEDER Geometrie-Synchronisation neu (nicht nur beim Ziehen) — sonst
// würde ein angedocktes Fenster von der Kante wegdriften, sobald sich seine
// eigene, jetzt automatisch aus dem Inhalt abgeleitete Grösse beim Zoomen
// ändert (oder sich die Kante selbst beim Pannen der Zeichnung verschiebt).
// Propagiert eine dadurch nötige Verschiebung an die eigene Kreuz-Gruppe
// weiter (siehe propagateGroupDrag) — sonst würde ein angedocktes Fenster
// beim reinen Zoomen/Pannen aus seiner Gruppe herausdriften, ganz ohne dass
// je gezogen wurde.
function applyDockedEdgeRePins() {
  windowsState.forEach(win => {
    if (!win.dockedEdge) return;
    // Die geteilte Kreuz-Achse eines Fensters (xMm bei COLUMN_LOCK_DIRECTIONS,
    // zMm bei ROW_LOCK_DIRECTIONS) darf nie andocken (siehe applyDocking) —
    // ein Fenster kann hier trotzdem theoretisch ein stehengebliebenes altes,
    // inzwischen ungültiges dockedEdge tragen (Altdaten aus einer Version vor
    // diesem Schutz); dann einfach löschen statt re-pinnen.
    const edgeIsX = win.dockedEdge === 'left' || win.dockedEdge === 'right';
    const edgeAllowed = edgeIsX ? !COLUMN_LOCK_DIRECTIONS.includes(win.direction) : !ROW_LOCK_DIRECTIONS.includes(win.direction);
    if (!edgeAllowed) { win.dockedEdge = null; return; }
    const snapshot = windowsState.map(w => ({ id: w.id, direction: w.direction, xMm: w.xMm, zMm: w.zMm }));
    const pinned = pinnedPositionForEdge(win.dockedEdge, win, lastBoundsMm);
    if (pinned.xMm !== null) win.xMm = pinned.xMm;
    if (pinned.zMm !== null) win.zMm = pinned.zMm;
    propagateGroupDrag(snapshot, win.id, win.xMm, win.zMm).forEach(patch => {
      const other = findWindow(patch.id);
      if (!other) return;
      if (patch.xMm !== undefined) other.xMm = patch.xMm;
      if (patch.zMm !== undefined) other.zMm = patch.zMm;
    });
  });
}

function syncViewWindowsGeometry(fitScale, boundsMm) {
  lastFitScale = fitScale;
  const prevBoundsMm = lastBoundsMm;
  lastBoundsMm = boundsMm || null;
  if (!layerEl) return;
  if (lastBoundsMm) {
    windowsState.forEach(win => Object.assign(win, autoSizeMm(win.direction, lastBoundsMm)));
    applyDockedEdgeRePins();
  }
  windowsState.forEach(win => {
    const el = layerEl.querySelector(`[data-view-window-id="${win.id}"]`);
    if (el) applyWindowGeometry(win, el);
  });
  if (windowsState.length && scaleContextChanged(prevBoundsMm, lastBoundsMm)) {
    scheduleRebakeAll();
  }
}

// ---------------------------------------------------------------------------
// Window chrome (DOM + einfache Pointer-Event-Zustandsmaschine)
// ---------------------------------------------------------------------------

function isSelected(id) {
  return selectedWindowId === id;
}

function findWindow(id) {
  return windowsState.find(win => win.id === id) || null;
}

// Pfeil statt Kompass-Buchstabe je Button: zeigt, auf welche Seite des
// Referenzfensters die neue Ansicht tatsächlich zu liegen kommt (First-Angle,
// siehe print_plan_projection.js) — z.B. der "Nord"-Button (weiterhin an der
// gewohnten Kompass-Position oben in diesem Raster) zeigt einen Pfeil nach
// UNTEN, weil die Nord-Ansicht First-Angle-konform unterhalb landet, nicht
// oberhalb. Aus derselben Platzierungs-Konfiguration abgeleitet, die auch
// adjacentWindowRect()/commitDockState() steuert, damit Button-Pfeil und
// tatsächliche Platzierung nie auseinanderlaufen können.
// Zeigt jeweils den Pfeil der GEGENÜBERLIEGENDEN Richtung (placementDirectionFor)
// — z.B. der "Nord"-Button zeigt einen Pfeil nach Süden (unten), weil die
// Nord-Ansicht First-Angle-konform dort landet; analog für die 4 ISO-Ecken
// (der "Südwest"-Button zeigt einen Pfeil nach Nordosten). Deckt sich exakt
// mit der Formel in adjacentWindowRect()/print_plan_projection.js — derselbe
// Pfeil zeigt also immer korrekt, wohin ein Klick die neue Ansicht legt.
const PLACEMENT_SIDE_ARROW = {
  north: '↑', south: '↓', east: '→', west: '←',
  northeast: '↗', northwest: '↖', southeast: '↘', southwest: '↙'
};
function directionArrow(direction) {
  return PLACEMENT_SIDE_ARROW[placementDirectionFor(direction)] || directionLabel(direction);
}

// 3x3-Kreuz-Raster: Draufsicht in der Mitte, N/S/O/W an den 4 Kreuz-Positionen,
// die 4 ISO-Diagonalansichten (NW/NO/SW/SO) an den 4 Ecken — Gitterposition
// entspricht direkt der Blickrichtung (wie bei N/S/O/W: der Button "oben
// links" erzeugt die Nordwest-Ansicht), nur die PLATZIERUNG der neuen Ansicht
// landet First-Angle-konform gegenüber (siehe directionArrow()-Kommentar).
// Klick erzeugt (siehe generateAdjacentWindow()) ein NEUES, an das
// ausgewählte Fenster angrenzendes Fenster in dieser Richtung — ändert
// bewusst NICHT die Richtung des ausgewählten Fensters selbst, daher kein
// "is-active"-Zustand auf den Zellen (kein Toggle, sondern eine Aktion).
function directionGridHtml(win) {
  const baseTitle = t('modal.drucken.wert.ansicht_erzeugen', 'Angrenzende Ansicht erzeugen');
  // has-window: hebt den Button der Blickrichtung des AKTUELL SELEKTIERTEN
  // Fensters hellorange hervor (gleiches Muster wie .has-dossier-content/
  // .has-i18n-gap, styles.css) — Diagnosehilfe, damit man nach dem
  // Verschieben/Umsortieren der Fenster trotzdem sofort sieht, welche
  // Blickrichtung das gerade markierte Fenster tatsächlich hat.
  const cell = (direction, content, isArrow) => direction
    ? `<button type="button" class="btn print-plan-sidebar-option print-plan-2d-direction-btn${isArrow ? ' print-plan-2d-direction-btn--arrow' : ''}${win.direction === direction ? ' has-window' : ''}" data-print-plan-view2d-direction="${direction}" title="${escapeHtml(`${baseTitle} (${directionLabel(direction)})`)}" aria-label="${escapeHtml(`${baseTitle} (${directionLabel(direction)})`)}"${direction === 'top' && win.direction === 'top' ? ' disabled' : ''}>${escapeHtml(content)}</button>`
    : '<span class="print-plan-2d-direction-empty"></span>';
  return `
    <div class="print-plan-sidebar-direction-grid">
      ${cell('northwest', directionArrow('northwest'), true)}${cell('north', directionArrow('north'), true)}${cell('northeast', directionArrow('northeast'), true)}
      ${cell('west', directionArrow('west'), true)}${cell('top', directionLabel('top'))}${cell('east', directionArrow('east'), true)}
      ${cell('southwest', directionArrow('southwest'), true)}${cell('south', directionArrow('south'), true)}${cell('southeast', directionArrow('southeast'), true)}
    </div>
  `;
}

function wallToggleHtml(win) {
  const isOn = win.filters.parametricWall === true;
  return `
    <div class="print-plan-sidebar-subtitle print-plan-sidebar-section--divided">${escapeHtml(t('modal.drucken.label.wand_parametrisch', 'Wand (parametrisch)'))}</div>
    <div class="print-plan-sidebar-card-options print-plan-sidebar-card-options--grid2">
      <button type="button" class="btn print-plan-sidebar-option${isOn ? ' is-active' : ''}" data-print-plan-view2d-wall="on">${escapeHtml(t('modal.drucken.wert.ein', 'Ein'))}</button>
      <button type="button" class="btn print-plan-sidebar-option${!isOn ? ' is-active' : ''}" data-print-plan-view2d-wall="off">${escapeHtml(t('modal.drucken.wert.aus', 'Aus'))}</button>
    </div>
  `;
}

function selectedWindowSettingsHtml() {
  const win = findWindow(selectedWindowId);
  if (!win) return '';
  const filterButtons = WINDOW_FILTER_KEYS.map(key => `<button type="button" class="btn print-plan-sidebar-option${win.filters[key] ? ' is-active' : ''}" data-print-plan-view2d-filter="${key}">${escapeHtml(filterLabel(key))}</button>`).join('');
  return `
    <div class="print-plan-sidebar-window-settings">
      <div class="print-plan-sidebar-subtitle">${escapeHtml(t('modal.drucken.label.ansicht_erzeugen', 'Ansicht erzeugen'))}</div>
      ${directionGridHtml(win)}
      <div class="print-plan-sidebar-subtitle print-plan-sidebar-section--divided">${escapeHtml(t('modal.drucken.label.ebenen', 'Ebenen'))}</div>
      <div class="print-plan-sidebar-card-options print-plan-sidebar-card-options--grid2">${filterButtons}</div>
      ${wallToggleHtml(win)}
    </div>
  `;
}

function rebakeSelectedWindow() {
  const win = findWindow(selectedWindowId);
  const el = layerEl?.querySelector(`[data-view-window-id="${selectedWindowId}"]`);
  if (!win || !el) return;
  queueBake(win, el);
}

function toggleSelectedWindowFilter(key) {
  const win = findWindow(selectedWindowId);
  if (!win || !WINDOW_FILTER_KEYS.includes(key)) return;
  win.filters[key] = !win.filters[key];
  onSidebarRefreshNeeded?.();
  onGeometryCommit?.();
  rebakeSelectedWindow();
}

function setSelectedWindowWallVisible(visible) {
  const win = findWindow(selectedWindowId);
  if (!win) return;
  const next = Boolean(visible);
  if (win.filters.parametricWall === next) return;
  win.filters.parametricWall = next;
  onSidebarRefreshNeeded?.();
  onGeometryCommit?.();
  rebakeSelectedWindow();
}

function renderWindowContent(win, el) {
  const host = el.querySelector('.print-plan-2d-window-canvas-host');
  if (!host) return;
  host.innerHTML = win.snapshot ? `<img alt="" draggable="false" src="${escapeHtml(win.snapshot)}">` : '';
}

function buildWindowElement(win) {
  const el = document.createElement('div');
  el.className = 'print-plan-2d-window';
  el.dataset.viewWindowId = win.id;
  el.innerHTML = `
    <div class="print-plan-2d-window-canvas-host"></div>
    <button type="button" class="print-plan-2d-window-delete" title="${escapeHtml(t('modal.drucken.wert.fenster_entfernen', 'Fenster entfernen'))}" aria-label="${escapeHtml(t('modal.drucken.wert.fenster_entfernen', 'Fenster entfernen'))}">&times;</button>
  `;
  renderWindowContent(win, el);
  applyWindowGeometry(win, el);

  el.addEventListener('pointerdown', event => handleWindowPointerDown(win, el, event));
  const deleteBtn = el.querySelector('.print-plan-2d-window-delete');
  deleteBtn.addEventListener('pointerdown', event => event.stopPropagation());
  deleteBtn.addEventListener('click', event => {
    event.stopPropagation();
    removeViewWindow(win.id);
  });

  return el;
}

// Wendet dockToBounds() auf die aktuelle Fensterposition an (falls Projekt-
// Bounds bekannt sind) und schreibt ein ggf. gefundenes Andocken direkt in
// win.xMm/zMm — dockState wird vom Aufrufer (startMove) über die gesamte
// Ziehgeste hinweg weitergereicht (Hysterese, siehe dockToBounds()).
//
// win.direction wird durchgereicht, damit dockToBounds die geteilte Kreuz-
// Achse (xMm bei Nord/Süd/Draufsicht, zMm bei West/Ost/Draufsicht) von den
// Andock-Kandidaten ausschliesst — diese Achse liegt per Konstruktion bereits
// exakt auf der entsprechenden Bounds-Kante (z.B. Nord erbt xMm/widthMm 1:1
// von der Draufsicht, deren xMm/widthMm selbst exakt boundsMm.left/right
// entsprechen), und ohne den Ausschluss würde jede unabhängige Bewegung auf
// der jeweils ANDEREN, eigentlich freien Achse diese unbewegte (Distanz 0)
// Achse fälschlich als "gerade angedockt" erkennen — liess Fenster beim
// Ziehen quer durchs Bild springen (siehe dockToBounds()' Kommentar).
function applyDocking(win, dockState) {
  if (!lastBoundsMm) return null;
  const dock = dockToBounds(dockState, win, lastBoundsMm, win.direction);
  if (dock) {
    if (dock.xMm !== null) win.xMm = dock.xMm;
    if (dock.zMm !== null) win.zMm = dock.zMm;
  }
  return dock;
}

// Nach Abschluss einer Ziehgeste: übernimmt ein ggf. geändertes Andocken in
// win.dockedEdge und schaltet die Blickrichtung passend zur Andock-Kante um
// (oben→Nord, unten→Süd, links→West, rechts→Ost) — "sobald ein 2D-Fenster
// beim Ziehen an einer Kante andockt, wird direction aktualisiert". Ohne
// aktives Andocken bleibt die zuletzt gesetzte Richtung unverändert. Bakt
// NICHT selbst (das entscheidet der Aufrufer, je nachdem ob er ohnehin schon
// einen Bake auslöst) — gibt nur zurück, ob sich die Richtung geändert hat.
function commitDockState(win, dockState) {
  const newEdge = dockState ? dockState.edge : null;
  if (win.dockedEdge === newEdge) return false;
  win.dockedEdge = newEdge;
  if (newEdge) {
    // DOCK_EDGE_DIRECTIONS liefert die reine Seiten-Tatsache ("oben = Nord-
    // Seite des Blatts"), unabhängig von der Projektionsmethode.
    // viewDirectionForPlacement() übersetzt das erst in die tatsächlich zu
    // zeigende Blickrichtung (First-Angle: gegenüberliegende Fassade), siehe
    // print_plan_projection.js.
    win.direction = viewDirectionForPlacement(DOCK_EDGE_DIRECTIONS[newEdge]);
    return true;
  }
  return false;
}

// Ordnet ein frei verschobenes Fenster NACH dem Loslassen automatisch der zur
// NEUEN Position passenden Blickrichtung zu, statt Verschiebungen auf die
// "falsche" Seite zu sperren — z.B. wird aus einer ursprünglich Süd-Ansicht
// automatisch eine Ost-Ansicht, sobald sie an die rechte Seite der Draufsicht
// gezogen wird (sonst stimmt die Projektion nicht mehr mit der Position
// überein). Läuft UNABHÄNGIG vom exakten Andocken (commitDockState) — das
// bleibt zusätzlich für die präzise Kanten-Ausrichtung zuständig. Die
// eigentliche Positions-Klassifikation (classifyDraufsichtSide) sitzt im
// importfreien print_plan_window_snap.js, damit sie automatisiert testbar
// bleibt (dieses Modul ist über print_plan_3d_view.js/app_state.js
// DOM-gekoppelt, siehe dessen Node-Isolations-Kommentar).
function reassignDirectionForPosition(win) {
  if (!lastBoundsMm || win.direction === 'top') return false;
  const side = classifyDraufsichtSide(win, lastBoundsMm);
  if (!side) return false;
  const nextDirection = viewDirectionForPlacement(side);
  if (nextDirection === win.direction) return false;
  win.direction = nextDirection;
  return true;
}

function handleWindowPointerDown(win, el, event) {
  if (event.button !== undefined && event.button !== 0) return;
  event.stopPropagation();
  startMove(win, el, event);
}

// Kreuz-Ausrichtung beim Ziehen (siehe COLUMN_LOCK_DIRECTIONS/
// ROW_LOCK_DIRECTIONS/propagateGroupDrag in print_plan_window_snap.js): ein
// Snapshot ALLER Fenster wird beim Geste-Start genommen (nicht nur des
// gezogenen), damit dx/dz je Tick relativ zum GESTE-START berechnet werden
// (kein Drift durch Aufsummieren vieler kleiner Ticks). Die DOM-Elemente der
// potenziellen Mitzieh-Kandidaten werden einmalig gecacht statt pro Tick neu
// gequeryt.
function startMove(win, el, event) {
  const pointerId = event.pointerId;
  const startScreen = { x: event.clientX, y: event.clientY };
  const startMm = { x: win.xMm, z: win.zMm };
  const startSnapshot = windowsState.map(w => ({ id: w.id, direction: w.direction, xMm: w.xMm, zMm: w.zMm }));
  const followerEls = new Map();
  windowsState.forEach(w => {
    if (w.id === win.id) return;
    const followerEl = layerEl?.querySelector(`[data-view-window-id="${w.id}"]`);
    if (followerEl) followerEls.set(w.id, followerEl);
  });
  let moved = false;
  let dockState = win.dockedEdge ? { edge: win.dockedEdge } : null;
  el.setPointerCapture(pointerId);

  const onMove = moveEvent => {
    if (moveEvent.pointerId !== pointerId) return;
    const dxPx = moveEvent.clientX - startScreen.x;
    const dyPx = moveEvent.clientY - startScreen.y;
    if (Math.hypot(dxPx, dyPx) > 2) moved = true;
    win.xMm = startMm.x + dxPx / lastFitScale;
    win.zMm = startMm.z + dyPx / lastFitScale;
    dockState = applyDocking(win, dockState);
    applyWindowGeometry(win, el);
    propagateGroupDrag(startSnapshot, win.id, win.xMm, win.zMm).forEach(patch => {
      const other = findWindow(patch.id);
      const otherEl = followerEls.get(patch.id);
      if (!other) return;
      if (patch.xMm !== undefined) other.xMm = patch.xMm;
      if (patch.zMm !== undefined) other.zMm = patch.zMm;
      if (otherEl) applyWindowGeometry(other, otherEl);
    });
  };
  const onUp = upEvent => {
    if (upEvent.pointerId !== pointerId) return;
    el.releasePointerCapture(pointerId);
    el.removeEventListener('pointermove', onMove);
    el.removeEventListener('pointerup', onUp);
    el.removeEventListener('pointercancel', onUp);
    if (!moved) {
      selectViewWindow(win.id);
    } else {
      // Präzises Andocken an eine Bounds-Kante gewinnt (exakte Ausrichtung);
      // erst wenn das Fenster NICHT exakt angedockt ist, entscheidet die
      // allgemeinere Positions-Klassifikation (reassignDirectionForPosition)
      // anhand dessen, auf welcher Seite der Draufsicht es aktuell liegt —
      // so muss man eine Verschiebung auf die "falsche" Seite nicht sperren,
      // die Blickrichtung folgt stattdessen der neuen Position.
      const dockChanged = commitDockState(win, dockState);
      const directionChanged = dockChanged ? false : reassignDirectionForPosition(win);
      if (dockChanged || directionChanged) {
        // Ändert win.direction — Grösse muss sofort für die neue Richtung
        // neu abgeleitet werden, statt auf den nächsten Zoom-/Pan-Tick zu warten.
        if (lastBoundsMm) Object.assign(win, autoSizeMm(win.direction, lastBoundsMm));
        applyWindowGeometry(win, el);
        onSidebarRefreshNeeded?.();
        queueBake(win, el);
      }
      onGeometryCommit?.();
    }
  };
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);
}

function removeViewWindow(id) {
  if (isSelected(id)) deselectViewWindow();
  windowsState = windowsState.filter(win => win.id !== id);
  layerEl?.querySelector(`[data-view-window-id="${id}"]`)?.remove();
  onGeometryCommit?.();
}

function selectViewWindow(id) {
  if (isSelected(id)) return;
  onBeforeSelect?.();
  if (selectedWindowId) layerEl?.querySelector(`[data-view-window-id="${selectedWindowId}"]`)?.classList.remove('is-selected');
  selectedWindowId = id;
  layerEl?.querySelector(`[data-view-window-id="${id}"]`)?.classList.add('is-selected');
  onSidebarRefreshNeeded?.();
}

function deselectViewWindow() {
  if (!selectedWindowId) return;
  const id = selectedWindowId;
  selectedWindowId = null;
  layerEl?.querySelector(`[data-view-window-id="${id}"]`)?.classList.remove('is-selected');
  onSidebarRefreshNeeded?.();
}

function isAnyViewWindowSelected() {
  return Boolean(selectedWindowId);
}

// ---------------------------------------------------------------------------
// Hidden-Line-Rendering: zweistufiges Rendern derselben S.view3d.scene mit
// einer orthografischen Kamera — Silhouette-Pass (weiss, gefüllter Tiefen-
// puffer) + Kanten-Pass (schwarze EdgesGeometry-Linien, gegen den Tiefenpuffer
// von Pass 1 getestet). Siehe Plan-Datei für die vollständige Herleitung.
// ---------------------------------------------------------------------------

// Pro Mesh-Geometrie gecachte EdgesGeometry — build3DScene()s eigene Signatur-
// basierte Wiederverwendung liefert bei unveränderten Presets/Filtern dieselben
// Geometrie-Objekte über mehrere Bakes hinweg (mehrere Richtungen/Fenster).
// Keine explizite Dispose-Buchhaltung nötig: WeakMap-Einträge werden automatisch
// unerreichbar, sobald build3DScene() echte neue Geometrien baut, und der
// frische, wegwerfbare WebGLRenderer pro Bake wird über
// releaseWebGLRenderer() mitsamt seinem GPU-Kontext explizit freigegeben.
const edgesGeometryCache = new WeakMap();
function cachedEdgesGeometry(THREE, geometry) {
  let cached = edgesGeometryCache.get(geometry);
  if (!cached) {
    cached = new THREE.EdgesGeometry(geometry);
    edgesGeometryCache.set(geometry, cached);
  }
  return cached;
}

// Läuft die Elternkette hoch bis zur Szenenwurzel — die parametrische Wand
// (sketch_wall_render3d.js) taggt nur ihre GRUPPE (root.userData.
// isParametricWallGroup), nicht jedes einzelne Mesh darunter.
function isInParametricWallGroup(object) {
  let node = object;
  while (node) {
    if (node.userData?.isParametricWallGroup) return true;
    node = node.parent;
  }
  return false;
}

// Objekte, die in der Strichzeichnung nichts verloren haben: bereits im Code
// getaggte Auswahl-/Ausrichtungs-Hilfsobjekte (scene3d.js), die (nur als
// Referenz gedachte) Grundriss-Hintergrundfläche, und — sofern das Fenster
// dies per "Wand (parametrisch)"-Schalter so will (Standard: aus) — die
// parametrische Wand.
function isExcludedFromBake(object, win) {
  const data = object.userData || {};
  if (data.is3DSelectionHelper || data.isAlignmentHelper || data.pickable === false || data.modalBackgroundFilter) return true;
  if (win?.filters?.parametricWall !== true && isInParametricWallGroup(object)) return true;
  return false;
}

// Zentrum/Blickrichtung/Sichtfeld je nach Richtung — "top" blickt senkrecht von
// oben (Draufsicht), die 4 Elevationen blicken waagrecht längs ±X/±Z. Norden
// zeigt konventionsgemäss (wie bereits bei Multischrank für "Türfront"
// verwendet, siehe multischrank_orientation.js) in Richtung -Z.
//
// MASSSTABGETREU (Fix für "verzogen"/"entspricht nicht den effektiven
// Massen"): viewWidth/viewHeight werden NICHT mehr unabhängig vom
// Druckmassstab an den gesamten Projektinhalt gefittet (das ergab bei jedem
// win.widthMm/heightMm, das nicht zufällig genau das Content-Seitenverhältnis
// hatte, eine verzerrte Kamera-Projektion gegenüber dem tatsächlichen
// Render-Ziel-Seitenverhältnis) — sondern IMMER exakt aus der eigenen
// Fenstergrösse * Druckmassstab abgeleitet (win.widthMm/heightMm sind
// Rahmen-mm/Papier-mm; *n ergibt die dazu gehörige Welt-mm-Grösse). Damit
// entspricht ein grösseres Fenster automatisch einem grösseren sichtbaren
// Weltausschnitt bei GLEICHBLEIBENDEM Massstab (wie ein echtes Fenster),
// statt bei jeder Grössenänderung neu in den Inhalt hineinzuzoomen.
//
// ZENTRIERUNG (Fix für "Zeichnung verschwindet beim Zoomen"): alle 5
// Richtungen zentrieren IMMER auf denselben Bezugspunkt — die Mitte von
// boundsMm (Papier-mm, dieselbe Quelle, aus der auch autoSizeMm() die
// Fenstergrösse ableitet), statt (früher bei Draufsicht/Nord/Süd) auf die
// Papier-POSITION des Fensters selbst. Dadurch zeigt die Kamera immer exakt
// denselben Weltausschnitt, den die (jetzt automatisch gleich grosse) Box
// auch abdeckt — unabhängig davon, wohin das Fenster auf der Seite gezogen
// wurde oder wie die Hauptvorschau gepannt/gezoomt ist; Box und Kamera können
// dadurch nie mehr auseinanderlaufen. `bounds` (S.view3d.bounds, die WAHRE
// volle 3D-Szenen-Ausdehnung inkl. Hindernissen/Skizzenwänden) bleibt
// weiterhin ausschliesslich für die Kamera-Sicherheitswerte (spanX/spanZ/
// maxY/distance/depthSpan, Near/Far sowie die Positionierung ENTLANG der
// Blickachse) zuständig — dafür muss die volle Szene bekannt sein, damit real
// gerenderte Geometrie nie am Kamera-Frustum clippt.
function directionCameraSetup(win, direction, bounds, boundsMm, n) {
  const worldWidthMm = Math.max(1, win.widthMm * n);
  const worldHeightMm = Math.max(1, win.heightMm * n);
  const maxY = Math.max(1, Number(bounds?.maxY) || 2000);
  const spanX = Math.max(100, bounds.maxX - bounds.minX);
  const spanZ = Math.max(100, bounds.maxZ - bounds.minZ);
  const distance = Math.max(spanX, spanZ, maxY, worldWidthMm, worldHeightMm) * 2 + 2000;

  const centerX = boundsMm.originWorldMm.x + ((boundsMm.left + boundsMm.right) / 2) * n;
  const centerZ = boundsMm.originWorldMm.z + ((boundsMm.top + boundsMm.bottom) / 2) * n;
  const centerY = (boundsMm.heightMm * n) / 2;

  if (direction === 'top') {
    return {
      viewWidth: worldWidthMm,
      viewHeight: worldHeightMm,
      position: { x: centerX, y: centerY + distance, z: centerZ },
      target: { x: centerX, y: 0, z: centerZ },
      up: { x: 0, y: 0, z: -1 },
      depthSpan: maxY + 500
    };
  }

  const up = { x: 0, y: 1, z: 0 };
  if (direction === 'north' || direction === 'south') {
    const target = { x: centerX, y: centerY, z: centerZ };
    return direction === 'north'
      ? { viewWidth: worldWidthMm, viewHeight: worldHeightMm, position: { x: centerX, y: centerY, z: bounds.minZ - distance }, target, up, depthSpan: spanZ + 500 }
      : { viewWidth: worldWidthMm, viewHeight: worldHeightMm, position: { x: centerX, y: centerY, z: bounds.maxZ + distance }, target, up, depthSpan: spanZ + 500 };
  }

  // ISO-Ecken: dieselbe "Kamera aussen, blickt auf Zentrum, up=Y" Logik wie
  // die 4 Haupt-Himmelsrichtungen, nur gleichzeitig auf X- UND Z-Achse
  // versetzt (diagDistance = distance/√2, damit der Gesamtabstand zum
  // Zentrum vergleichbar mit den geraden Elevationen bleibt statt durch die
  // doppelte Versetzung unnötig weit hinauszuwachsen). depthSpan deckt die
  // volle Grundriss-Diagonale ab, da Near/Far hier keiner einzelnen Achse
  // mehr folgen.
  if (DIAGONAL_DIRECTIONS.includes(direction)) {
    const target = { x: centerX, y: centerY, z: centerZ };
    const diagDistance = distance / Math.SQRT2;
    const x = direction === 'northeast' || direction === 'southeast' ? bounds.maxX + diagDistance : bounds.minX - diagDistance;
    const z = direction === 'southeast' || direction === 'southwest' ? bounds.maxZ + diagDistance : bounds.minZ - diagDistance;
    return { viewWidth: worldWidthMm, viewHeight: worldHeightMm, position: { x, y: centerY, z }, target, up, depthSpan: Math.hypot(spanX, spanZ) + 500 };
  }

  const target = { x: centerX, y: centerY, z: centerZ };
  return direction === 'east'
    ? { viewWidth: worldWidthMm, viewHeight: worldHeightMm, position: { x: bounds.maxX + distance, y: centerY, z: centerZ }, target, up, depthSpan: spanX + 500 }
    : { viewWidth: worldWidthMm, viewHeight: worldHeightMm, position: { x: bounds.minX - distance, y: centerY, z: centerZ }, target, up, depthSpan: spanX + 500 };
}

// Wendet NUR die Sichtbarkeits-Filter dieses Fensters an (Preset/Helligkeit/
// Sonnenstand bleiben unangetastet, siehe Modul-Kopfkommentar) — gleiches
// Grundmuster wie print_plan_3d_view.js's applyAmbientSceneState(): temporär
// auf S.view3d.modalFilters schreiben, build3DScene() neu bauen lassen, danach
// den ursprünglichen Ambient-Zustand wiederherstellen, damit die geteilte
// Szene (auch vom Haupt-3D-Modal genutzt) nach dem Bake sauber bleibt.
async function applyWindowFilters(win, modules) {
  const ambientFilters = { ...modules.scene3d.normalize3DModalFilters(S.view3d) };
  S.view3d.modalFilters = { ...win.filters };
  await modules.scene3d.build3DScene({ preserveCamera: true });
  modules.scene3d.apply3DModalSceneFilters();
  return ambientFilters;
}

async function restoreAmbientFilters(ambientFilters, modules) {
  S.view3d.modalFilters = ambientFilters;
  await modules.scene3d.build3DScene({ preserveCamera: true });
  modules.scene3d.apply3DModalSceneFilters();
}

// Dreht eine fertige Canvas um `degrees` (im Uhrzeigersinn) auf eine neue
// Canvas — bei ±90° werden Breite/Höhe vertauscht (Ausgabe-Canvas hat dann
// height×width statt width×height). Reines 2D-Context-drawImage, funktioniert
// unabhängig davon, ob die Quelle eine WebGL- oder 2D-Canvas ist (siehe
// canvasToBlob() in snapshot_controller.js, das ebenfalls nur die generische
// HTMLCanvasElement-API nutzt).
function rotateCanvasImage(sourceCanvas, degrees) {
  const normalized = ((degrees % 360) + 360) % 360;
  if (normalized === 0) return sourceCanvas;
  const swapDims = normalized === 90 || normalized === 270;
  const outW = swapDims ? sourceCanvas.height : sourceCanvas.width;
  const outH = swapDims ? sourceCanvas.width : sourceCanvas.height;
  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate((normalized * Math.PI) / 180);
  ctx.drawImage(sourceCanvas, -sourceCanvas.width / 2, -sourceCanvas.height / 2);
  return out;
}

function objectEffectivelyVisible(object, root) {
  let current = object;
  while (current && current !== root?.parent) {
    if (current.visible === false) return false;
    if (current === root) break;
    current = current.parent;
  }
  return true;
}

function visibleSceneGeometryBounds(THREE, root, fallback) {
  if (!THREE?.Box3 || !root?.traverse) return fallback;
  root.updateWorldMatrix?.(true, true);
  const bounds = new THREE.Box3();
  const objectBounds = new THREE.Box3();
  let found = false;
  root.traverse(object => {
    if (!(object?.isMesh || object?.isLine || object?.isLineSegments)) return;
    if (!objectEffectivelyVisible(object, root)) return;
    objectBounds.setFromObject(object);
    if (objectBounds.isEmpty()) return;
    bounds.union(objectBounds);
    found = true;
  });
  if (!found || bounds.isEmpty()) return fallback;
  return {
    ...fallback,
    minX: bounds.min.x,
    maxX: bounds.max.x,
    minY: bounds.min.y,
    maxY: bounds.max.y,
    minZ: bounds.min.z,
    maxZ: bounds.max.z
  };
}

function selectedSceneAndPaperBounds(selection, sceneBounds, paperBounds) {
  const selectedBounds = selectedPrintPlanGroupBounds({
    selectedGroupKeys: selection.selectedGroupKeys
  });
  if (!selectedBounds) return { sceneBounds, paperBounds };
  const nextSceneBounds = {
    ...sceneBounds,
    minX: selectedBounds.minX,
    maxX: selectedBounds.maxX,
    minZ: selectedBounds.minZ,
    maxZ: selectedBounds.maxZ
  };
  const n = Math.max(0.0001, Number(paperBounds?.n) || 1);
  const origin = paperBounds?.originWorldMm || { x: 0, z: 0 };
  const nextPaperBounds = {
    ...paperBounds,
    left: (selectedBounds.minX - origin.x) / n,
    right: (selectedBounds.maxX - origin.x) / n,
    top: (selectedBounds.minZ - origin.z) / n,
    bottom: (selectedBounds.maxZ - origin.z) / n,
    depthMm: Math.max(1, (selectedBounds.maxZ - selectedBounds.minZ) / n),
    heightMm: Math.max(1, ((Number(nextSceneBounds.maxY) || 0) - (Number(nextSceneBounds.minY) || 0)) / n)
  };
  return { sceneBounds: nextSceneBounds, paperBounds: nextPaperBounds };
}

async function renderHiddenLineSnapshot(win, widthPx, heightPx, selection = currentPrintGroupSelection()) {
  const modules = await load2DModules();
  const THREE = await modules.scene3d.ensure3D();
  const ambientFilters = await applyWindowFilters(win, modules);
  const printGroupFilter = applyPrintPlanSceneGroupFilter(S.view3d?.projectRoot3D, {
    selectedGroupKeys: selection.selectedGroupKeys
  });

  const scene = S.view3d.scene;
  const baseBounds = visibleSceneGeometryBounds(
    THREE,
    S.view3d?.projectRoot3D,
    S.view3d.bounds || { minX: -1000, maxX: 1000, minZ: -1000, maxZ: 1000, maxY: 2000 }
  );
  const baseBoundsMm = lastBoundsMm || FALLBACK_BOUNDS_MM;
  const { sceneBounds: bounds, paperBounds: boundsMm } = selectedSceneAndPaperBounds(selection, baseBounds, baseBoundsMm);
  const n = boundsMm.n;
  const setup = directionCameraSetup(win, win.direction, bounds, boundsMm, n);

  // ±90°-Richtungen werden VOR der Rotation transponiert gerendert (Frustum
  // UND Pixel-Grösse vertauscht) — Kamera/Geometrie bleiben dabei exakt wie
  // von directionCameraSetup() bestimmt (siehe WINDOW_IMAGE_ROTATION_DEG-
  // Kommentar), nur so trifft das nachträglich gedrehte Ergebnis wieder
  // exakt widthPx×heightPx statt die Fenstergrösse zu verzerren.
  const rotationDeg = WINDOW_IMAGE_ROTATION_DEG[win.direction] || 0;
  const swapDims = Math.abs(rotationDeg) === 90;
  const renderWidthPx = swapDims ? heightPx : widthPx;
  const renderHeightPx = swapDims ? widthPx : heightPx;
  const renderViewWidth = swapDims ? setup.viewHeight : setup.viewWidth;
  const renderViewHeight = swapDims ? setup.viewWidth : setup.viewHeight;
  const aspect = renderViewWidth / renderViewHeight;

  const camera = modules.scene3d.create3DCamera(THREE, 'orthographic', aspect, { viewHeight: renderViewHeight });
  camera.position.set(setup.position.x, setup.position.y, setup.position.z);
  camera.up.set(setup.up.x, setup.up.y, setup.up.z);
  camera.lookAt(setup.target.x, setup.target.y, setup.target.z);
  const distanceToTarget = camera.position.distanceTo(new THREE.Vector3(setup.target.x, setup.target.y, setup.target.z));
  camera.near = Math.max(1, distanceToTarget - setup.depthSpan);
  camera.far = distanceToTarget + setup.depthSpan;
  camera.updateProjectionMatrix();

  let renderer = null;
  const previousBackground = scene.background;
  const hiddenObjects = [];
  const materialSwaps = [];
  let whiteMaterial = null;
  let edgeMaterial = null;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setClearColor(0xffffff, 1);
    renderer.setSize(Math.max(1, renderWidthPx), Math.max(1, renderHeightPx), false);

    scene.background = null;
    scene.updateMatrixWorld(true);

    // --- Pass 1: weisse, ungeschattete Silhouette (füllt den Tiefenpuffer) ---
    whiteMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      depthWrite: true,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1
    });

    scene.traverse(object => {
      if (!object.isMesh) return;
      if (!objectEffectivelyVisible(object, scene)) return;
      if (isExcludedFromBake(object, win)) { hiddenObjects.push(object); return; }
      object.children?.forEach(child => { if (child.isLineSegments) hiddenObjects.push(child); });
      if (Array.isArray(object.material)) {
        materialSwaps.push({ object, original: object.material });
        object.material = object.material.map(() => whiteMaterial);
      } else {
        materialSwaps.push({ object, original: object.material });
        object.material = whiteMaterial;
      }
    });
    hiddenObjects.forEach(object => {
      object.userData.__2dBakeHidden = object.visible;
      object.visible = false;
    });

    renderer.autoClear = true;
    renderer.render(scene, camera);

    materialSwaps.forEach(({ object, original }) => { object.material = original; });
    materialSwaps.length = 0;

    // --- Pass 2: schwarze Kantenlinien, gegen Pass 1s Tiefenpuffer getestet ---
    // Eigene, frische Overlay-Szene statt derselben Szene ein zweites Mal
    // umzubauen — hat standardmässig background=null, umgeht damit strukturell
    // Three.js' forceClear-bei-Farb-Background-Verhalten, das sonst Pass 1s
    // Tiefenpuffer unbemerkt wieder löschen würde.
    const edgesScene = new THREE.Scene();
    edgeMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });
    const instanceMatrix = new THREE.Matrix4();
    scene.traverse(object => {
      if (!object.isMesh
        || !objectEffectivelyVisible(object, scene)
        || hiddenObjects.includes(object)
        || isExcludedFromBake(object, win)) return;
      if (object.isInstancedMesh) {
        const edges = cachedEdgesGeometry(THREE, object.geometry);
        for (let i = 0; i < object.count; i++) {
          object.getMatrixAt(i, instanceMatrix);
          const seg = new THREE.LineSegments(edges, edgeMaterial);
          seg.matrixAutoUpdate = false;
          seg.matrix.multiplyMatrices(object.matrixWorld, instanceMatrix);
          edgesScene.add(seg);
        }
        return;
      }
      const edges = cachedEdgesGeometry(THREE, object.geometry);
      const seg = new THREE.LineSegments(edges, edgeMaterial);
      seg.matrixAutoUpdate = false;
      seg.matrix.copy(object.matrixWorld);
      edgesScene.add(seg);
    });

    renderer.autoClear = false;
    renderer.render(edgesScene, camera);
    renderer.autoClear = true;

    const rotatedCanvas = rotateCanvasImage(renderer.domElement, rotationDeg);
    return await modules.snapshotController.captureCanvasPngDataUrl(rotatedCanvas);
  } finally {
    materialSwaps.forEach(({ object, original }) => { object.material = original; });
    hiddenObjects.forEach(object => {
      object.visible = object.userData.__2dBakeHidden;
      delete object.userData.__2dBakeHidden;
    });
    scene.background = previousBackground;
    releaseWebGLRenderer(renderer);
    whiteMaterial?.dispose();
    edgeMaterial?.dispose();
    printGroupFilter.restore();
    await restoreAmbientFilters(ambientFilters, modules);
  }
}

// Live-Vorschau-Auflösung: früher ein fixer 420px-Kantenkappen-Wert,
// unabhängig von der tatsächlichen Bildschirmgrösse des Fensters — bei einem
// gross gezogenen oder stark hineingezoomten Fenster wurde das gebackene Bild
// dann sichtbar hochskaliert ("extrem pixelig/unscharf"). Jetzt an die
// AKTUELLE CSS-Pixelgrösse gebunden (win.widthMm/heightMm * lastFitScale,
// inkl. devicePixelRatio für Retina-Displays), mit Ober-/Untergrenze gegen
// unnötig teure Renders bzw. degenerierte Mini-Canvases.
const PLACEHOLDER_MIN_EDGE_PX = 80;
const PLACEHOLDER_MAX_EDGE_PX = 1400;
function renderPlaceholderSnapshot(win, selection = currentPrintGroupSelection()) {
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const rawWidth = win.widthMm * lastFitScale * dpr;
  const rawHeight = win.heightMm * lastFitScale * dpr;
  // Ein einziger, uniformer Skalierungsfaktor für beide Kanten — sonst würde
  // ein unabhängiges Klemmen pro Achse (z.B. eine breite, flache Ansicht) das
  // Seitenverhältnis leicht verschieben und gegen die Kamera-Rahmung
  // (fest an win.widthMm/heightMm gebunden) erneut verzerren.
  const maxEdge = Math.max(rawWidth, rawHeight);
  const scale = maxEdge > PLACEHOLDER_MAX_EDGE_PX ? PLACEHOLDER_MAX_EDGE_PX / maxEdge
    : maxEdge < PLACEHOLDER_MIN_EDGE_PX ? PLACEHOLDER_MIN_EDGE_PX / maxEdge
    : 1;
  return renderHiddenLineSnapshot(
    win,
    Math.max(1, Math.round(rawWidth * scale)),
    Math.max(1, Math.round(rawHeight * scale)),
    selection
  );
}

// Sicherheits-Obergrenze für die Druck-Kantenlänge in Pixeln — echter Bug-
// Fund: bei 200 DPI ergibt bereits eine ein paar Meter breite Wand (z.B.
// 4188mm) eine Zielbreite von >30'000px, weit jenseits von
// gl.MAX_TEXTURE_SIZE (in dieser Session gemessen: 8192, sowohl in einer
// SwiftShader-Sandbox als auch potenziell auf schwächerer Hardware wie dem
// eigentlichen Ziel-Raspberry-Pi) — der WebGL-Renderer bekam dadurch eine
// Canvas-Grösse gesetzt, die der Treiber gar nicht unterstützt, wodurch der
// Bake nie fertig wurde (im echten Druck: nur der leere Fensterrahmen ohne
// Bild). Analog zu renderPlaceholderSnapshot() wird EIN gemeinsamer, einmalig
// abgefragter und gecachter Skalierungsfaktor auf beide Kanten angewendet
// (Seitenverhältnis bleibt exakt erhalten) — degradiert die effektive DPI nur
// für unüblich grosse Projekte, statt den kompletten Druck fehlschlagen zu
// lassen.
let maxRenderEdgePxPromise = null;
function queryMaxRenderEdgePx() {
  if (!maxRenderEdgePxPromise) {
    // 10% Sicherheitsabstand — manche Treiber melden ihr theoretisches
    // Maximum, ohne bei exakt diesem Wert noch zuverlässig zu rendern.
    maxRenderEdgePxPromise = maxWebGLRenderEdgePx(S.view3d?.renderer);
  }
  return maxRenderEdgePxPromise;
}

async function renderWindowForPrint(win, selection = currentPrintGroupSelection()) {
  const modules = await load2DModules();
  await modules.scene3d.ensure3D();
  const maxEdgePx = await queryMaxRenderEdgePx();
  const rawWidth = (win.widthMm / MM_PER_INCH) * PRINT_WINDOW_SNAPSHOT_DPI;
  const rawHeight = (win.heightMm / MM_PER_INCH) * PRINT_WINDOW_SNAPSHOT_DPI;
  const maxEdge = Math.max(rawWidth, rawHeight);
  const scale = maxEdge > maxEdgePx ? maxEdgePx / maxEdge : 1;
  const width = Math.max(1, Math.round(rawWidth * scale));
  const height = Math.max(1, Math.round(rawHeight * scale));
  return renderHiddenLineSnapshot(win, width, height, selection);
}

// Läuft über dieselbe queueSceneWork()-Queue wie die 3D-Fenster (siehe
// Import-Kommentar oben) — verhindert sowohl gleichzeitig offene
// WebGLRenderer als auch, dass ein 2D- und ein 3D-Bake sich gegenseitig
// S.view3d.modalFilters überschreiben.
function queueBake(win, el) {
  const lifecycleGeneration = viewWindowLifecycleGeneration;
  const selection = currentPrintGroupSelection();
  return queueSceneWork(async () => {
    if (!windowsState.includes(win)) return;
    try {
      const dataUrl = await renderPlaceholderSnapshot(win, selection);
      if (!windowsState.includes(win)
        || lifecycleGeneration !== viewWindowLifecycleGeneration
        || selection.signature !== selectedPrintGroupSignature) return;
      win.snapshot = dataUrl;
      win.snapshotUpdatedAt = Date.now();
      win.snapshotGroupSelectionSignature = selection.signature;
      renderWindowContent(win, el);
      onGeometryCommit?.();
    } catch (error) {
      console.error('2D-Fenster konnte nicht gerendert werden', error);
    }
  });
}

function addViewWindow() {
  const win = createViewWindow();
  windowsState.push(win);
  const el = buildWindowElement(win);
  layerEl?.appendChild(el);
  onGeometryCommit?.();
  queueBake(win, el);
  return win;
}

function setViewWindowSelectedGroupKeys(values, options = {}) {
  const normalized = normalizePrintPlanGroupKeys(values);
  const signature = printPlanGroupSelectionSignature(normalized);
  if (signature === selectedPrintGroupSignature) return false;
  selectedPrintGroupKeys = normalized;
  selectedPrintGroupSignature = signature;
  if (options.invalidate === false) return true;

  viewWindowLifecycleGeneration += 1;
  windowsState.forEach(win => {
    win.snapshot = null;
    win.snapshotUpdatedAt = null;
    win.snapshotGroupSelectionSignature = null;
    const el = layerEl?.querySelector(`[data-view-window-id="${win.id}"]`);
    if (!el) return;
    renderWindowContent(win, el);
    queueBake(win, el);
  });
  onGeometryCommit?.();
  return true;
}

// ---------------------------------------------------------------------------
// Mount / lifecycle (von print_plan_modal.js aufgerufen)
// ---------------------------------------------------------------------------

function mountViewWindows(layer, savedWindows, callbacks = {}) {
  viewWindowLifecycleGeneration += 1;
  selectedPrintGroupKeys = normalizePrintPlanGroupKeys(callbacks.selectedGroupKeys);
  selectedPrintGroupSignature = printPlanGroupSelectionSignature(selectedPrintGroupKeys);
  layerEl = layer;
  windowsState = normalizeViewWindows(savedWindows);
  onGeometryCommit = typeof callbacks.onGeometryCommit === 'function' ? callbacks.onGeometryCommit : null;
  onSidebarRefreshNeeded = typeof callbacks.onSidebarRefreshNeeded === 'function' ? callbacks.onSidebarRefreshNeeded : null;
  onBeforeSelect = typeof callbacks.onBeforeSelect === 'function' ? callbacks.onBeforeSelect : null;
  selectedWindowId = null;
  if (layerEl) {
    layerEl.innerHTML = '';
    windowsState.forEach(win => {
      if (!snapshotMatchesPrintGroupSelection(win)) {
        win.snapshot = null;
        win.snapshotUpdatedAt = null;
        win.snapshotGroupSelectionSignature = null;
      }
      const el = buildWindowElement(win);
      layerEl.appendChild(el);
      if (!win.snapshot) queueBake(win, el);
    });
  }
}

function disposeViewWindows() {
  viewWindowLifecycleGeneration += 1;
  if (rebakeAllTimer) {
    clearTimeout(rebakeAllTimer);
    rebakeAllTimer = null;
  }
  deselectViewWindow();
  layerEl = null;
  windowsState = [];
  onGeometryCommit = null;
  onSidebarRefreshNeeded = null;
  onBeforeSelect = null;
}

// ---------------------------------------------------------------------------
// Druck-Bake: ein frischer, wegwerfbarer Renderer pro Fenster, sequenziell
// (nicht Promise.all — gleiche WebGL-Context-Erschöpfungs-Lektion wie bei den
// 3D-Fenstern). Bewusst OHNE Rahmen am <img> (im Gegensatz zu den 3D-Fenstern),
// damit die Strichzeichnung optisch mit der Zeichnung verschmilzt.
async function viewWindowsPrintHtml() {
  if (!windowsState.length) return '';
  const images = [];
  const selection = currentPrintGroupSelection();
  for (const win of windowsState) {
    try {
      const dataUrl = await queueSceneWork(() => renderWindowForPrint(win, selection));
      images.push(`<img style="position:absolute;left:${win.xMm}mm;top:${win.zMm}mm;width:${win.widthMm}mm;height:${win.heightMm}mm;object-fit:cover" src="${dataUrl}">`);
    } catch (error) {
      console.error('2D-Fenster konnte nicht für den Druck gerendert werden', error);
    }
  }
  return images.join('');
}

export {
  normalizeViewWindows,
  serializeViewWindows,
  viewsSidebarButtonHtml,
  selectedWindowSettingsHtml,
  mountViewWindows,
  disposeViewWindows,
  syncViewWindowsGeometry,
  selectViewWindow,
  deselectViewWindow,
  isAnyViewWindowSelected,
  addViewWindow,
  generateAdjacentWindow,
  toggleSelectedWindowFilter,
  setSelectedWindowWallVisible,
  setViewWindowSelectedGroupKeys,
  viewWindowsPrintHtml,
  directionLabel,
  DIRECTIONS
};
